import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Shield,
  User,
  Server,
  Route,
  Building2,
  Store,
  ExternalLink,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

// ─── Live BaseScan proofs (mirrored from src/data/honestStatus.js) ─────────
const BASESCAN = 'https://basescan.org/address';
const PROOFS = {
  withdrawalGuard: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E',
  staking: '0xaae9ae4d52fe13384522e0ef4114b0a5f5d62d68',
  koji: '0xb50007B292256E2e54253D2110013465f9441482',
  harvestVault: '0xB6a79B470D480A436aC59837Ec07F36FafE0c4C5',
  guardianPause: '0x74642654bFf0AB1F22d8747396cb1eC217f04Db3',
  sepoliaHookBake: '0x5662668FB18bc504e05f4809005f8caa104103BA',
};

// ─── Paulo Falcão's five-link ADL-M, with Kojiru mapped on top ─────────────
const LINKS = [
  {
    id: 'principal',
    n: 1,
    icon: User,
    accent: 'text-blue-300',
    ring: 'border-blue-500/25 bg-blue-500/[0.03]',
    linkName: 'Principal',
    linkSub: 'The human or company on whose behalf the agent acts',
    todayDefault:
      'Wears their own agent’s errors. Authorized a mandate, not this purchase. Only Amex’s closed loop covers registered-agent errors today; four-party networks do not.',
    kojiruFix:
      'WithdrawalGuard enforces a zero-discretion cap on the commit leg. A principal literally cannot lose more than the pre-agreed ceiling in a 24h window. The contract cannot be persuaded into "yes."',
    proofLabel: 'WithdrawalGuard on Base',
    proofAddr: PROOFS.withdrawalGuard,
    proofNote: 'Live · enforcing · 10%/24h rate-limit',
  },
  {
    id: 'provider',
    n: 2,
    icon: Server,
    accent: 'text-violet-300',
    ring: 'border-violet-500/25 bg-violet-500/[0.03]',
    linkName: 'Agent Provider',
    linkSub: '39% of the market thinks they should pay; 0% of their ToS agrees',
    todayDefault:
      'No regulation anywhere makes an agent developer stand behind its agent’s purchases. The gap between what the market expects and what the contracts say is the widest in the chain.',
    kojiruFix:
      'ACS™ is a per-agent-id credit score backed by a USDC bond posted before the agent is allowed to transact. The provider’s balance sheet is on the line per-transaction. No bond, no ACSHook consent: no execution.',
    proofLabel: 'KojiruStakingV2 on Base',
    proofAddr: PROOFS.staking,
    proofNote: 'Live · BaseScan source-verified · user stakes active',
  },
  {
    id: 'rails',
    n: 3,
    icon: Route,
    accent: 'text-emerald-300',
    ring: 'border-emerald-500/25 bg-emerald-500/[0.03]',
    linkName: 'Rails',
    linkSub: 'x402, AP2, AgentKit, wallets: standardize how value moves; underwrite nothing',
    todayDefault:
      'An August 2026 audit indexed 272 x402 services across two public directories; roughly 12% (33 hosts) name different chains outright: Base vs. Arbitrum, Solana vs. Base, Sepolia vs. Amoy. That is the field an agent reads to route USDC. No rail today underwrites the outcome, and none can guarantee which chain the outcome happened on.',
    kojiruFix:
      'Kojiru is native at the tool layer, upstream of the rails: a Streamable-HTTP MCP server plus @kojiru/agentkit and @kojiru/langchain wrappers. Every payment is gated by the ACSHook, which reads block.chainid directly rather than a directory: the chain cannot lie about which chain it is. AEP-consumable verification-attestation format proposed to the A-Comm working group (Aug 2026 comment round).',
    proofLabel: 'MCP Registry listing',
    proofAddr: null,
    proofHref:
      'https://registry.modelcontextprotocol.io/servers/io.github.faulknerwayne73-droid%2Fkojiru-mcp',
    proofNote: 'io.github.faulknerwayne73-droid/kojiru-mcp · Bearer-authenticated',
  },
  {
    id: 'network',
    n: 4,
    icon: Building2,
    accent: 'text-amber-300',
    ring: 'border-amber-500/25 bg-amber-500/[0.03]',
    linkName: 'Network & Issuer',
    linkSub: 'Amex answers this in a closed loop; four-party networks cannot',
    todayDefault:
      'Expect identity to keep improving here (card-network agent programs). Expect indemnity to keep not arriving: three balance sheets, none of them volunteering.',
    kojiruFix:
      'Kojiru is issuer-independent. The breach is recorded on-chain next to the operator\u2019s pledge in the same transaction as the proof. No network or issuer needs to volunteer their balance sheet. Kojiru provides the evidence, not the remedy.',
    proofLabel: 'Sepolia ACSHook slash bake',
    proofAddr: PROOFS.sepoliaHookBake,
    proofNote:
      'Pre-mainnet bake completed · rotation drill + slash test verified · mainnet 2026-07-20',
  },
  {
    id: 'merchant',
    n: 5,
    icon: Store,
    accent: 'text-red-300',
    ring: 'border-red-500/25 bg-red-500/[0.03]',
    linkName: 'Merchant',
    linkSub: 'The default owner of everything unassigned',
    todayDefault:
      '324 million disputes/yr by 2028. Cannot distinguish agent from human at checkout. Cannot evidence the intent behind the order it fulfilled. In every liability regime ever built, losses pool at the party with the least evidence: today, that is this link.',
    kojiruFix:
      'This is Kojiru’s flagship position. The merchant holds a signed mandate, a public operator pledge, and an on-chain evidence record for the erroneous purchase. The pledge is a public commitment signal, not a promise of repayment. Remedies stay with the merchant&rsquo;s existing dispute policy.',
    proofLabel: 'HarvestVault on Base',
    proofAddr: PROOFS.harvestVault,
    proofNote: 'Live · revenue distribution rail · awaiting first slash routing event',
  },
];

// ─── Recourse-layer thesis strip (mirrors /mcp x402 comparison) ────────────
const THESIS = [
  {
    title: 'A-Comm AEP',
    role: 'Witness',
    verb: 'proves what happened',
    answer: 'the log is untampered',
    ring: 'border-white/10 bg-white/[0.02]',
    accent: 'text-gray-300',
  },
  {
    title: 'Kojiru Recourse Layer™',
    role: 'Consequence',
    verb: 'proves what happens next',
    answer: 'the breach is recorded next to the operator\u2019s public pledge',
    ring: 'border-red-500/40 bg-gradient-to-br from-red-500/10 to-red-500/[0.02]',
    accent: 'text-red-300',
  },
];

function ProofPill({ label, addr, href, note }) {
  const url = href || (addr ? `${BASESCAN}/${addr}` : null);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-start gap-2 mt-4 group"
      data-testid={`adlm-proof-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 group-hover:border-emerald-500/40 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">
            Live proof
          </span>
          <span className="text-xs text-white font-semibold">{label}</span>
          <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-emerald-300 transition-colors ml-auto" />
        </div>
        {addr && (
          <div className="text-[11px] font-mono text-gray-400 mt-1 break-all">
            {addr}
          </div>
        )}
        {note && (
          <div className="text-[11px] text-gray-500 mt-1 leading-snug">{note}</div>
        )}
      </div>
    </a>
  );
}

export default function AdlMap() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="adlm-page">
      <Helmet>
        <title>
          The Agentic Delegation Liability Map | Kojiru: The Recourse Layer™
        </title>
        <meta
          name="description"
          content="Paulo Falcão's five-link liability map, with the Kojiru Recourse Layer slotted into each link. Every claim on this page is verifiable on BaseScan today."
        />
      </Helmet>

      <Navbar />

      {/* ─── HERO ───────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-16 px-6" data-testid="adlm-hero">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-6"
              data-testid="adlm-hero-badge"
            >
              <Shield className="w-3.5 h-3.5" />
              For merchants, PSPs, and risk officers
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
              data-testid="adlm-hero-headline"
            >
              Everyone standardized how agents pay.{' '}
              <br className="hidden sm:block" />
              <span className="text-red-300">
                Nobody standardized who pays for the agents.
              </span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              Paulo Falcão’s{' '}
              <em className="text-white">Agentic Delegation Liability Map</em> walks
              a bad agent payment through five links and asks, at each one, who
              eats the loss today. Read the map and one rule falls out:{' '}
              <span className="text-white font-semibold">
                losses default to whoever holds the least evidence.
              </span>{' '}
              Here is the same map with the Kojiru Recourse Layer™ slotted
              into each link, and a BaseScan address next to every claim.
            </p>
            <div className="mt-6 text-xs text-gray-500 leading-relaxed">
              Framework: Paulo Falcão,{' '}
              <em>“165 Million Agent Payments. No One Has Agreed Who Pays for the Mistakes”</em>{' '}
              (Aug 10, 2026). Evidence layer: A-Comm Evidence Protocol (AEP)
              v1.0.3-rc.2, in public comment through Aug 14, 2026. Kojiru is a{' '}
              <em>downstream consumer</em> of AEP evidence, not a competitor to
              it.
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── THE FIVE LINKS ─────────────────────────────────────────────── */}
      <section
        className="py-12 px-6 border-t border-white/5"
        data-testid="adlm-links-section"
      >
        <div className="container mx-auto max-w-5xl">
          <div className="mb-10">
            <div className="text-[11px] font-mono tracking-[0.22em] text-red-400 mb-3">
              // ADL-M · THE FIVE LINKS
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              Walk the chain. Name the party. Show the proof.
            </h2>
          </div>

          <div className="space-y-6" data-testid="adlm-links-grid">
            {LINKS.map((l, i) => {
              const Icon = l.icon;
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`rounded-2xl border ${l.ring} p-6 sm:p-8`}
                  data-testid={`adlm-link-${l.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div
                      className={`flex-shrink-0 w-11 h-11 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center ${l.accent}`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-1 ${l.accent}`}
                      >
                        Link {l.n}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                        {l.linkName}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">{l.linkSub}</p>
                    </div>
                  </div>

                  {/* Two-column: today vs Kojiru */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className="rounded-xl border border-white/10 bg-black/30 p-4"
                      data-testid={`adlm-link-${l.id}-today`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2">
                        <AlertTriangle className="w-3 h-3" />
                        Today’s default
                      </div>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {l.todayDefault}
                      </p>
                    </div>
                    <div
                      className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4"
                      data-testid={`adlm-link-${l.id}-kojiru`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-2">
                        <CheckCircle2 className="w-3 h-3" />
                        With Kojiru
                      </div>
                      <p className="text-sm text-gray-200 leading-relaxed">
                        {l.kojiruFix}
                      </p>
                      <ProofPill
                        label={l.proofLabel}
                        addr={l.proofAddr}
                        href={l.proofHref}
                        note={l.proofNote}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── RECOURSE-LAYER THESIS ──────────────────────────────────────── */}
      <section
        className="py-16 px-6 border-t border-white/5"
        data-testid="adlm-thesis-section"
      >
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs uppercase tracking-widest text-emerald-300 mb-4">
              <Shield className="w-3.5 h-3.5" />
              The category shift
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              Evidence stops you from being blamed for the loss.{' '}
              <span className="text-red-300">
                It does not stop the loss.
              </span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              A-Comm’s AEP repairs the evidence asymmetry Paulo names. Without
              a recourse layer sitting downstream, the party with a perfect AEP
              record still absorbs the loss.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="adlm-thesis-grid">
            {THESIS.map((t) => (
              <div
                key={t.title}
                className={`rounded-2xl border ${t.ring} p-6`}
                data-testid={`adlm-thesis-${t.role.toLowerCase()}`}
              >
                <div
                  className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${t.accent} mb-3`}
                >
                  {t.role}
                </div>
                <div className="text-xl font-semibold text-white mb-2 leading-snug">
                  {t.title}
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {t.verb} → 
                  <em className="text-white not-italic font-semibold">
                    {t.answer}
                  </em>
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-500 mt-8 max-w-2xl mx-auto">
            The two are architecturally orthogonal. Kojiru’s <code className="text-emerald-400">records</code>{' '}
            and <code className="text-emerald-400">anchor</code> MCP tools emit
            AEP-compatible artifacts by design. Adopt both.
          </p>
        </div>
      </section>

      {/* ─── ONE-LINE TEST ─────────────────────────────────────────────── */}
      <section
        className="py-16 px-6 border-t border-white/5 bg-white/[0.02]"
        data-testid="adlm-test-section"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <div className="text-[11px] font-mono tracking-[0.22em] text-red-400 mb-4">
            // THE TEN-SECOND TEST
          </div>
          <blockquote className="text-2xl sm:text-3xl font-semibold leading-tight text-white max-w-3xl mx-auto mb-4">
            “Name the party that eats the loss when an agent buys wrong on
            your rails.”
          </blockquote>
          <p className="text-lg text-gray-400 mb-6">
            If your answer is <em>“it depends”</em> it defaults to you.
          </p>
          <p className="text-base text-white font-semibold">
            Kojiru’s answer: <span className="text-emerald-300">the agent’s bond does.</span>{' '}
            That’s the entire product.
          </p>
        </div>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6" data-testid="adlm-cta-section">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
              Put a name at every link before an analyst does it for you.
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Kojiru is live on Base mainnet, source-verified on BaseScan, and
              distributed through the MCP registry plus AgentKit and a popular agent framework
              packages. If agents can commit funds anywhere in your architecture,
              the recourse layer is the missing link, and it can be wired
              in without touching your existing rails.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="adlm-cta-book">
                <Button
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2"
                >
                  Book a 25-minute risk briefing
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/compliance" data-testid="adlm-cta-compliance">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 gap-2"
                >
                  Full compliance walkthrough
                </Button>
              </Link>
              <Link to="/mcp" data-testid="adlm-cta-mcp">
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-white hover:bg-white/5 gap-2"
                >
                  MCP + SDK surfaces
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
